import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { OrganisationService } from '@services/organisations/organisation.service';
import { ActivatedRoute, Router } from '@angular/router';
import { GroupHasRoleInOrganisationService } from '@services/groups/group-has-role-in-organisation.service';
import { GroupHasProfileService } from '@services/groups/group-has-profile.service';
import { ProfileService } from '@services/profiles/profile.service';
import { GroupService } from '@services/groups/group.service';
import { GroupHasSecuredAppService } from '@services/groups/group-has-secured-app.service';
import { GroupHasContentService } from '@services/groups/group-has-content.service';
import { TranslateService } from '@services/translate/translate.service';
import { DialogService } from '@services/dialog/dialog.service';
import { GroupHasFrontProfileService } from '@services/groups/group-has-frontprofile.service';
import { StorageService } from '@services/storage/storage.service';
import { PageAction } from '../../../components/page-actions/page-actions.component';
import { MessengerService } from '@services/messenger/messenger.service';
import { PackageService } from '@services/packages/package.service';
import { FrontProfilesService } from '@services/front-profiles/front-profiles.service';
import moment from 'moment/moment';
import { Models } from '@ibexfusion/models';
import { DataTableComponent } from '../../../components/data-table/data-table.component';
import { FrontProfileExportWizardComponent } from '../../../components/popUpSelects/front-profile-export-wizard/front-profile-export-wizard.component';
import * as _ from 'lodash';
import { AdditionalProfileDataService } from '@services/additional-profile-data/additional-profile-data.service';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-user-admin-detail',
  templateUrl: './user-admin-detail.component.html',
  styleUrls: ['./user-admin-detail.component.scss'],
  providers: [OrganisationService, GroupHasRoleInOrganisationService, GroupHasProfileService, GroupService, ProfileService, GroupHasSecuredAppService, GroupHasContentService,
    GroupHasFrontProfileService, PackageService, FrontProfilesService, AdditionalProfileDataService],
  standalone: false
})
export class UserAdminDetailComponent implements OnInit, OnDestroy {
  @ViewChild(DataTableComponent) dataTable: DataTableComponent;

  public navigationPoints: string[];
  public navigationPointOrder = ['profiles', 'groups', 'frontend-profiles', 'frontend-groups', 'addressbooks'];
  activeNavigation = 'profiles';
  title = 'Benutzerverwaltung'
  id: string;
  frontProfileParams;
  groupParams;
  frontGroupParams;
  addressbookParams;
  profileParams;
  addressbookRoles = ['chatModuleGroupAdmin', 'chatModule'];
  frontGroupRoles = ['securedContent', 'ticketingModule', 'ticketingModuleEditor', 'orderModule'];
  headerButtons = [{
    icon: 'add', callback: () => {
      return this.add()
    }, tooltipTranslation: 'userAdmin.addTooltips.' + this.activeNavigation
  }];
  pageActions;

  frontProfileCheckboxActions: any[];
  profileCheckboxActions: any[];

  public groupColumns = [{
    prop: 'name',
    valueTransformer: (val) => (val || '').startsWith('pref_') ? val.split('_').splice(2, val.split('_').length - 1).join('_') : val,
    translationString: 'userAdmin.frontend-group.name'
  }];
  public profileColumns = [{ prop: 'email' }, { prop: 'firstName' }, { prop: 'lastName' }];
  public frontProfileColumns = [
    { prop: 'lastName', translationString: 'myUsers.lastName' },
    { prop: 'firstName', translationString: 'myUsers.firstName' },
    { prop: 'email', translationString: 'myUsers.channel.email' },
    { prop: 'mobile', translationString: 'myUsers.channel.mobile' },
    {
      prop: 'isActivated', chip: true, translationPrefix: 'myUsers.states.', sortField: 'lastSeen', valueCallback: this.checkActiveAndLastSeen,
      translationString: 'myUsers.isActivated', hover: (data) => {
        let val = this.translations?.myUsers?.states?.[this.checkActiveAndLastSeen(data)];
        return val + (!!data.lastSeen ? (' - ' + moment(data.lastSeen).format('DD.MM.YYYY - HH:mm')) : '');
      }
    }
  ];
  loading = true;
  translations;
  packageIds = [];
  location;
  storageData;

  roleMapping = {
    chatModuleAdmin: ['addressbooks', 'frontend-profiles'], backendUserAdmin: ['groups', 'profiles'], ticketingModuleAdmin: ['frontend-groups', 'frontend-profiles'],
    orderModuleAdmin: ['frontend-groups', 'frontend-profiles'], securedContentAdmin: ['frontend-groups', 'frontend-profiles'],
    notificationModuleAdmin: ['frontend-groups', 'frontend-profiles'], operation: ['groups', 'profiles', 'frontend-groups', 'addressbooks', 'frontend-profiles']
  };

  constructor(
    private organisationService: OrganisationService, private route: ActivatedRoute, private ghrioService: GroupHasRoleInOrganisationService,
    private ghpService: GroupHasProfileService, public profileService: ProfileService, public groupService: GroupService, private router: Router,
    private ghsaService: GroupHasSecuredAppService, private ghcService: GroupHasContentService, private ts: TranslateService, private dialogService: DialogService,
    private ghfpService: GroupHasFrontProfileService, private storage: StorageService, private messenger: MessengerService, private pakService: PackageService,
    public frontProfileService: FrontProfilesService, private dialog: MatDialog, private additionalDataService: AdditionalProfileDataService
  ) {
    this.messenger.$activeRoles.subscribe((roles: any) => {
      this.initNavigationPoints(roles);
    });
  }

  initNavigationPoints(roles) {
    const navigationPoints = [];
    roles?.map(el => el.roleName).forEach(r => {
      if (this.roleMapping[r]) {
        navigationPoints.push(...this.roleMapping[r]);
      }
    });
    this.navigationPoints = [...new Set(navigationPoints)].sort((a, b) => this.navigationPointOrder.indexOf(a) - this.navigationPointOrder.indexOf(b));
    this.activeNavigation = this.storageData?.activeNavigation ? this.storageData?.activeNavigation : this.navigationPoints[0];
  }

  async ngOnInit() {
    this.location = this.router.url;
    this.storageData = await this.storage.getData('data-table-filter-' + this.location);
    if (this.router.url.includes('user-admin')) {
      this.pageActions = [
        new PageAction({
          icon: 'subdirectory_arrow_left', title: 'Back', translationString: 'general.back',
          callback: () => {
            this.router.navigate([this.router.url.split('/')[1]])
          }
        })
      ]
    }
    this.activeNavigation = this.storageData?.activeNavigation ? this.storageData?.activeNavigation : 'groups';
    this.translations = this.ts.get();
    this.id = this.route.snapshot.params['id'] || this.messenger.$activeOrganisation.getValue().OrganisationID.toString();
    const org = await this.organisationService.get(this.id);
    this.title = this.title + ': ' + org.title;
    const ghrio = await this.ghrioService.find(0, 1000, { OrganisationID: this.id });
    ghrio.data = ghrio.data.filter(el => el.GroupID.toString() !== '8');
    const groupIds = [], addressbookIds = [], frontGroupIds = [];
    ghrio.data.map(el => {
      if (this.frontGroupRoles.includes(el.roleName)) {
        frontGroupIds.push(el.GroupID);
      } else if (this.addressbookRoles.includes(el.roleName)) {
        addressbookIds.push(el.GroupID)
      } else {
        groupIds.push(el.GroupID);
      }
    });
    const childOrgs = (await this.organisationService.find(0, 1000, { ParentOrganisationID: this.id })).data;
    let profileGroupIds = JSON.parse(JSON.stringify(groupIds));
    if (childOrgs?.length) {
      let childGhrio = (await this.ghrioService.find(0, 1000, { OrganisationID: { $in: childOrgs.map(el => el.OrganisationID) } })).data;
      childGhrio = childGhrio.filter(el => !this.frontGroupRoles.concat(this.addressbookRoles).includes(el.roleName) && el.GroupID.toString() !== '8');
      profileGroupIds = profileGroupIds.concat(childGhrio.map(el => el.GroupID));
    }
    if (this.route.snapshot.params['packageId']) {
      this.packageIds = [this.route.snapshot.params['packageId']];
    } else {
      const packages: any = await this.pakService.find(0, 100, { OwnerOrganisationID: { $in: [...childOrgs.map(el => el.OrganisationID.toString()), this.id] } });
      this.packageIds = packages?.data?.map(pak => pak._id).filter(el => !!el);
    }
    this.frontProfileParams = { packageId: { $in: this.packageIds }, isAnonymous: false };
    const ghp = await this.ghpService.find(0, 1000, { GroupID: { $in: profileGroupIds }, groupIsPersonal: 0 });
    this.groupParams = { GroupID: groupIds.length ? { $in: groupIds } : -2, isPersonal: false };
    this.addressbookParams = { GroupID: addressbookIds.length ? { $in: addressbookIds } : -2, isPersonal: false };
    this.frontGroupParams = { GroupID: frontGroupIds.length ? { $in: frontGroupIds } : -2, isPersonal: false };
    this.profileParams = { ProfileID: { $in: ghp.data.map(el => el.ProfileID) } };
    this.initNavigationPoints(this.messenger.$activeRoles.getValue());
    this.initCheckboxActions();
    this.loading = false;
  }

  async ngOnDestroy() {

  }

  initCheckboxActions() {
    ['frontProfile', 'profile'].forEach(key => {
      this[key + 'CheckboxActions'] = [{
        title: this.translations.userAdmin.removeUsersTitle, icon: 'delete', idField: key.charAt(0).toUpperCase() + key.slice(1) + 'ID', callback: (ids: string[]) => {
          this['delete' + key.charAt(0).toUpperCase() + key.slice(1) + 's'](ids)
        }
      }];
      if (key === 'profile') {
        this[key + 'CheckboxActions'].push({
          title: this.translations.userAdmin.invitationLinkTitle, icon: 'email', callback: (elements: Models.FrontProfileModel[]) => { this.sendInvitationMails(elements) }
        });
      } else if (key === 'frontProfile') {
        this[key + 'CheckboxActions'].push({
          title: this.translations.userAdmin.exportUsersTitle, icon: 'cloud_download', callback: (profiles: Models.FrontProfileModel[]) => { this.startExportWizard(profiles) }
        });
      }
    });
  }

  async deleteFrontProfiles(ids: string[]) {
    let text = this.translations.userAdmin.removeUsersRequestTextMulti1 + ' ' + ids.length + ' ' + this.translations.userAdmin.removeUsersRequestTextMulti2;
    if (ids.length === 1) {
      text = this.translations.userAdmin.removeUsersRequestTextSingle
    }
    const dialogRes = await this.dialogService.confirm(text, this.translations.userAdmin.removeUsersRequestTitle);
    if (dialogRes !== true) {
      return Promise.resolve();
    }
    for (const id of ids) {
      await this.frontProfileService.remove(id);
    }
    this.dataTable.unselectAll();
    return this.dataTable.page();
  }

  async deleteProfiles(ids) {
    let text = this.translations.userAdmin.removeUsersRequestTextMulti1 + ' ' + ids.length + ' ' + this.translations.userAdmin.removeUsersRequestTextMulti2;
    if (ids.length === 1) {
      text = this.translations.userAdmin.removeUsersRequestTextSingle
    }
    const dialogRes = await this.dialogService.confirm(text, this.translations.userAdmin.removeUsersRequestTitle);
    if (dialogRes !== true) {
      return Promise.resolve();
    }
    for (const id of ids) {
      await this.profileService.remove(id);
    }
    this.dataTable.unselectAll();
    return this.dataTable.page();
  }

  async sendInvitationMails(profiles: any[]) {
    const apiKey = 'lXvM6ebAR6|gm0GRcxp20p4zG9nvir2Fxqbv3d6fhRRGNRXo9b9w6eF/Ht+WafSPJRgeAwklPivvUxqleBXPAkgIn8+2z2EIw==';
    for (let profile of profiles) {
      this.messenger.activateAccount(profile, apiKey);
    }
  }

  private startExportWizard(profiles) {
    const dialogRef = this.dialog.open(FrontProfileExportWizardComponent, { data: { packageId: this.route.snapshot.params['packageId'] }, width: '800px' });
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.length) {
        const fields = result.filter(el => el.selected);
        this.generateExcel(fields, profiles);
      }
    });
  }

  private async generateExcel(fields: any[], profiles: Models.FrontProfileModel[]) {
    if (!profiles.length) return;
    const finalProfiles = await Promise.all(profiles.map(async (profile) => {
      const finalProfile = {};
      const additionalData = profile.additionalDataId ? await this.additionalDataService.get(profile.additionalDataId) : null;
      for (const field of fields) {
        const name = field.name;
        const additionalValue = additionalData?.data?.[name];
        finalProfile[name] = additionalValue !== undefined ? additionalValue : name === 'state' ? this.translations.userAdmin.states[await this.checkActiveAndLastSeen(profile)] : profile[name];
      }
      return finalProfile;
    }));

    const escapeCsvValue = (value: any): string => {
      return /["\n\r,]/.test((value == null ? '' : String(value))) ? `"${(value == null ? '' : String(value)).replace(/"/g, '""')}"` : (value == null ? '' : String(value));
    };
    const header = fields.map(f => escapeCsvValue(f.title || _.get(this.translations, f.translation)));
    const dataRows = finalProfiles.map(profile => fields.map(f => escapeCsvValue(profile[f.name])));
    const csv = [header, ...dataRows].map(row => row.join(';')).join('\r\n');
    const csvContent = '\uFEFF' + csv;
    const encodedUri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent);
    const link = Object.assign(document.createElement('a'), {
      href: encodedUri, download: `profile-export-${moment().format('DD-MM-YYYY HH-mm-ss')}.csv`
    });
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }


  selected(event: any) {
    const params = [this.route.snapshot.params['packageId'] || this.id, this.activeNavigation, event.row.GroupID || event.row.ProfileID || event.row.FrontProfileID];
    params.unshift(this.router.url.split('/')[1]);
    this.router.navigate(params);
  }

  async deleteGroup(event: any) {
    const ghp = await this.ghpService.find(0, 1000, { GroupID: event.row.GroupID });
    const ghfp = await this.ghfpService.find(0, 1000, { GroupID: event.row.GroupID });
    const ghrio = await this.ghrioService.find(0, 1000, { GroupID: event.row.GroupID });
    const ghsa = await this.ghsaService.find(0, 1000, { Groups_GroupID: event.row.GroupID });
    const ghc = await this.ghcService.find(0, 1000, { Groups_GroupID: event.row.GroupID });

    const title = event.row.name + ' ' + this.translations.userAdmin.group.deleteTitle;
    let message;
    if (this.activeNavigation === 'groups') {
      message = this.translations.userAdmin.group.deleteText1 + '<br><br>';
      message += ghp.data.length ? ghp.data.length + ' Profilen<br>' : '';
      message += ghfp.data.length ? ghfp.data.length + ' Frontend Profilen<br>' : '';
      message += ghsa.data.length ? ghsa.data.length + ' Geschützten Inhalten<br>' : '';
      message += ghc.data.length ? ghc.data.length + ' Inhalten<br>' : '';
      message += '<br>' + this.translations.userAdmin.group.deleteText2;
    }
    const dialogRes = await this.dialogService.confirm(message, title);
    if (!dialogRes) {
      return;
    }
    const ghpPromises = [];
    for (const el of ghp.data) {
      ghpPromises.push(this.ghpService.remove(el.ID.toString()));
    }
    await Promise.all(ghpPromises);
    const ghfpPromises = [];
    for (const el of ghfp.data) {
      ghfpPromises.push(this.ghfpService.remove(el.ID.toString()));
    }
    await Promise.all(ghfpPromises);
    const ghrioPromises = [];
    for (const el of ghrio.data) {
      ghrioPromises.push(this.ghrioService.remove(el.ID.toString()));
    }
    await Promise.all(ghrioPromises);
    const ghsaPromises = [];
    for (const el of ghsa.data) {
      ghsaPromises.push(this.ghsaService.remove(el.GroupHasSecuredAppID.toString()));
    }
    await Promise.all(ghsaPromises);
    const ghcPromises = [];
    for (const el of ghc.data) {
      ghcPromises.push(this.ghcService.remove(el.GroupHasContentID.toString()));
    }
    await Promise.all(ghcPromises);
    await this.groupService.remove(event.row.GroupID);
    this.ngOnInit();
  }

  async deleteProfile(event: any) {
    const ghp = await this.ghpService.find(0, 1000, { ProfileID: event.row.ProfileID });
    const title = this.translations.userAdmin.profile.deleteTitle;
    const message = this.translations.userAdmin.profile.deleteText;
    const dialogRes = await this.dialogService.confirm(message, title);
    if (!dialogRes) {
      return;
    }
    const ghpPromises = [];
    for (const el of ghp.data) {
      ghpPromises.push(this.ghpService.remove(el.ID.toString()));
    }
    await Promise.all(ghpPromises);
    await this.profileService.remove(event.row.ProfileID);
    this.ngOnInit();
  }

  async deleteFrontProfile(event: any) {
    const ghrio = await this.ghrioService.find(0, 1000, { roleName: 'chatModuleGroupAdmin', OrganisationID: this.id });
    const ghfp = await this.ghfpService.find(0, 1000, { ProfileID: event.row.FrontProfileID });
    const isAdmin = ghrio.data.filter(a => ghfp.data.find(g => g.GroupID.toString() === a.GroupID.toString()))?.length;
    if (isAdmin) {
      const adminRes = await this.dialogService.confirm(this.translations.userAdmin.profile.deleteAdmin);
      if (!adminRes) return;
    }
    const title = this.translations.userAdmin.profile.deleteTitle;
    const message = this.translations.userAdmin.profile.deleteText;
    const dialogRes = await this.dialogService.confirm(message, title);
    if (!dialogRes) {
      return;
    }

    const ghfpPromises = [];
    for (const el of ghfp.data) {
      ghfpPromises.push(this.ghfpService.remove(el.ID.toString()));
    }
    await Promise.all(ghfpPromises);
    await this.frontProfileService.remove(event.row.FrontProfileID);
    this.ngOnInit();
  }

  add() {
    const params = [this.route.snapshot.params['packageId'] || this.id, this.activeNavigation];
    params.unshift(this.router.url.split('/')[1]);
    this.router.navigate(params);
  }

  updateNavigation(navigation) {
    this.activeNavigation = navigation;
    this.storage.setData('data-table-filter-' + this.location, { activeNavigation: navigation });

    this.headerButtons = [{
      icon: 'add', callback: () => {
        return this.add()
      }, tooltipTranslation: 'userAdmin.addTooltips.' + navigation
    }];
  }

  checkActiveAndLastSeen(data: Models.FrontProfileModel, additionalData?): any {
    if (!data.isActivated && !data.lastSeen) {
      return 'red';
    } else {
      const date = moment().milliseconds(0).seconds(0).minutes(0).hours(0);
      const lastSeen = moment(data.lastSeen);
      date.subtract(30, 'd');
      if (date < lastSeen) {
        return 'green';
      }
      date.subtract(30, 'd');
      if (date < lastSeen) {
        return 'yellow';
      }
      date.subtract(30, 'd');
      if (date < lastSeen) {
        return 'orange';
      } else {
        return 'dark-orange';
      }
    }
  }
}
